import { Checkbox } from '@components/ui/checkbox';
import { Input } from '@components/ui/input';
import { Badge } from '@components/ui/badge';
import type { MaterialItem, Unit } from '@generated/types';
import { displayToLbs, lbsToDisplayNumber } from '@generated/utils/units';

interface MaterialRowProps {
  material: MaterialItem;
  unit: Unit;
  checked: boolean;
  weightLbs: number | null;
  onToggle: (checked: boolean) => void;
  onWeightChange: (lbs: number | null) => void;
  invalid?: boolean;
}

// Keys that would let a "number" input type a negative sign, scientific notation, or a second
// decimal point — blocked outright rather than caught after the fact.
const BLOCKED_WEIGHT_KEYS = new Set(['-', '+', 'e', 'E']);

export function MaterialRow({ material, unit, checked, weightLbs, onToggle, onWeightChange, invalid }: MaterialRowProps) {
  const displayValue =
    weightLbs == null
      ? ''
      : unit === 'tons'
        ? lbsToDisplayNumber(weightLbs, 'tons').toFixed(2)
        : String(Math.round(lbsToDisplayNumber(weightLbs, 'lbs')));

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border p-3 transition-colors sm:flex-row sm:items-center ${
        invalid
          ? 'border-destructive/60 bg-destructive/5'
          : checked
            ? 'border-primary/40 bg-secondary'
            : 'border-border bg-card'
      }`}
    >
      <div className="flex flex-1 items-start gap-3">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onToggle(v === true)}
          className="mt-0.5"
          aria-label={`Include ${material.name}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-words text-sm font-medium text-foreground">{material.name}</span>
            {material.requiresUpload ? (
              <Badge variant="outline" className="border-primary/40 text-[10px] text-primary">
                Upload required
              </Badge>
            ) : null}
          </div>
          {material.examples ? (
            <p className="mt-0.5 break-words text-xs text-muted-foreground">{material.examples}</p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5 pl-8 sm:pl-0">
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          step={unit === 'tons' ? 0.01 : 1}
          disabled={!checked}
          aria-invalid={invalid}
          value={displayValue}
          onKeyDown={(e) => {
            if (BLOCKED_WEIGHT_KEYS.has(e.key)) e.preventDefault();
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text');
            if (!/^\d*\.?\d*$/.test(pasted.trim())) e.preventDefault();
          }}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onWeightChange(null);
              return;
            }
            const num = Number(raw);
            if (Number.isNaN(num) || num < 0) return;
            onWeightChange(displayToLbs(num, unit));
          }}
          className="w-24 text-right tabular-nums sm:w-28"
          aria-label={`Monthly weight for ${material.name}`}
        />
        <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
}
